import React from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import { SR_BUTTON_THEME_ADOBE_GREY } from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';

import './SrSunlocksSelfTouringModal.less';

const SrSunlocksSelfTouringModal = ({ onClose }) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  return (
    <SrModalV2
      theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
      buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
      onClose={onClose}
      contentPadding='26px 20px 260px'
    >
      <SrBox styleName='SrSunlocksSelfTouringModal'>
        <SrBox paddingRight='48px'>
          <SrText
            size='30'
            lineHeight='36px'
            display='block'
            weight='800'
          >
            Smart Locks for Self Touring
          </SrText>
        </SrBox>
        <SrBox marginTop='8px'>
          <SrText
            display='block'
            opacity='0.6'
          >
            For any vacant property that you lease with Sunroom, you have the option to allow self tours.
            &#8202;
            <SrText
              weight='800'
            >
              Allowing self tours helps renters tour the property when it's most convenient
            </SrText>
            &#8202;
            - which is often evenings and weekends. Utilizing Sunlock smart locks for self tours is a great
            option if you want to drive higher demand and faster lease times for the property.
          </SrText>
        </SrBox>
        <SrBox marginTop='10px' overflow='hidden'>
          <iframe
            width='100%'
            height={isSmallScreen ? '190px' : '400px'}
            src='https://www.youtube.com/embed/hzX4YPzRM3I'
            title='YouTube video player'
            frameBorder='0'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
          />
        </SrBox>
        <SrBox marginTop='22px'>
          <SrText
            size='21'
            weight='800'
          >
            How is the Sunlock installed?
          </SrText>
        </SrBox>
        <SrBox marginTop='6px'>
          <ol>
            <li>You select to enable self tours at the property.</li>
            <li>You and/or your client leaves a key at the property.</li>
            <li>
              A certified Sunroom installer goes out to the property and switches out the existing
              lock with a Sunlock. (We store the original lock in the kitchen cabinet
              of the home. Sunroom will switch the locks back after the property goes off market.)
            </li>
            <li>
              The installer sets up a wifi-enabled device in the home to connect the Sunlock to the
              Sunroom server. The Sunlock is matched to the property.
            </li>
            <li>
              The installer adds informational placards to the front door with access instructions and
              a unique url for renters. Once this is done, the property is ready for self tours.
            </li>
          </ol>
        </SrBox>
        <SrBox marginTop='32px'>
          <img
            width={isSmallScreen ? '100%' : '450px'}
            height='auto'
            alt='sunlock placard'
            src='/images/sunlock-placard.png'
            style={{ borderRadius: '14px' }}
          />
        </SrBox>
        <SrBox marginTop='8px'>
          <SrText
            fontStyle='italic'
            opacity='0.6'
          >
            A placard we put on the front door to instruct prospective renters on how to tour the property.
          </SrText>
        </SrBox>
        <SrBox marginTop='24px'>
          <SrText
            size='21'
            weight='800'
          >
            How does a self tour work?
          </SrText>
        </SrBox>
        <SrBox marginTop='6px'>
          <SrText opacity='0.6'>
            Only renters registered with Sunroom are able to self tour Sunroom properties. We ask them to
            upload a form of photo identification to confirm their identity for safety and security purposes.
          </SrText>
        </SrBox>
        <SrBox marginTop='24px'>
          <SrText opacity='0.6'>
            When a renter shows up to a property, they are prompted to follow the instructions on the door and
            click ‘visit this home’ from their phone. Their profile and request for access is matched with the
            property’s Sunlock, and the door will open automatically.
          </SrText>
        </SrBox>
        <SrBox marginTop='56px'>
          <SrText
            size='30'
            lineHeight='36px'
            weight='800'
          >
            Who can access the property through the Sunlock?
          </SrText>
        </SrBox>
        <SrBox marginTop='24px'>
          <SrText
            size='21'
            weight='800'
          >
            How will
            &#8202;
            <SrText
              size='21'
              weight='800'
              decoration='underline'
            >
              renters
            </SrText>
            &#8202;
            access the property?
          </SrText>
        </SrBox>
        <SrBox marginTop='8px'>
          <SrText opacity='0.6'>
            Renters will request to tour the property via the Sunroom app and website. Renters do not receive
            a key. Instead, they unlock it digitally upon completion of security steps. Upon leaving the
            property, the renter will be asked for property feedback.
          </SrText>
        </SrBox>
        <SrBox marginTop='32px'>
          <SrText
            size='21'
            weight='800'
          >
            How will
            &#8202;
            <SrText
              size='21'
              weight='800'
              decoration='underline'
            >
              vendors
            </SrText>
            &#8202;
            access the property?
          </SrText>
        </SrBox>
        <SrBox marginTop='8px'>
          <SrText opacity='0.6'>
            Each lock has a specific code you can share with vendors or the owners of the property.
            You’ll see that code on the partner portal on the listing.
          </SrText>
        </SrBox>
        <SrBox marginTop='32px'>
          <SrText
            size='21'
            weight='800'
          >
            How will
            &#8202;
            <SrText
              size='21'
              weight='800'
              decoration='underline'
            >
              real estate agents
            </SrText>
            &#8202;
            access the property?
          </SrText>
        </SrBox>
        <SrBox marginTop='8px'>
          <SrText opacity='0.6'>
            {
              'Real estate agents do not need to visit the Sunroom app or website to unlock the property. ' +
              'Instead, we’ll set up a Supra lockbox. Inside, the agents will find a key for the ' +
              'Sunlock.'
            }
          </SrText>
        </SrBox>
        <SrBox marginTop='56px'>
          <SrText
            size='30'
            lineHeight='36px'
            weight='800'
          >
            What are the safety and security precautions taken with Sunlocks?
          </SrText>
        </SrBox>
        <SrBox marginTop='24px'>
          <SrText
            size='21'
            weight='800'
          >
            Restricted access times
          </SrText>
        </SrBox>
        <SrBox marginTop='8px'>
          <SrText opacity='0.6'>
            The property can only be toured 8 am to 8 pm daily.
          </SrText>
        </SrBox>
        <SrBox marginTop='32px'>
          <SrText
            size='21'
            weight='800'
          >
            Auto locks and timestamps
          </SrText>
        </SrBox>
        <SrBox marginTop='8px'>
          <SrText opacity='0.6'>
            The Sunlock auto locks every 30 seconds. Every access is time-stamped and Sunroom
            closely monitors open/close activity.
          </SrText>
        </SrBox>
        <SrBox marginTop='32px'>
          <SrText
            size='21'
            weight='800'
          >
            Renter identity
          </SrText>
        </SrBox>
        <SrBox marginTop='8px'>
          <SrText opacity='0.6'>
            Renters validate their phone number through a text authentication system and upload a
            photo of their ID. Renters must complete liability questions before entering the property.
            Additionally, we verify that renters are at the property before unlocking the door.
          </SrText>
        </SrBox>
        <SrBox marginTop='56px'>
          <SrText
            size='30'
            lineHeight='36px'
            weight='800'
          >
            Property Turns
          </SrText>
        </SrBox>
        <SrBox marginTop='24px'>
          <SrText
            size='21'
            weight='800'
          >
            What happens once the property is rented?
          </SrText>
        </SrBox>
        <SrBox marginTop='8px'>
          <SrText opacity='0.6'>
            This lock is only in use while the property is getting leased. Once a lease is signed, within
            1-3 days we’ll trigger a “take-down” job for our field operations team. We will switch the Sunlock
            to your original lock.
          </SrText>
        </SrBox>
        <SrBox marginTop='24px'>
          <SrText opacity='0.6'>
            If you need to change the lock faster, you can remove the Sunlock and store it in the kitchen
            cabinet until we’re ready to pick it up.
          </SrText>
        </SrBox>
        <SrBox marginTop='56px'>
          <SrText
            size='30'
            lineHeight='36px'
            weight='800'
          >
            What's the hardware in a Sunlock?
          </SrText>
        </SrBox>
        <SrBox
          marginTop='10px'
          backgroundSize='contain'
          backgroundPosition={isSmallScreen ? 'center' : 'left'}
          backgroundRepeat='no-repeat'
          backgroundImage='url(/images/sunlocks.jpg)'
          height={isSmallScreen ? '253px' : '450px'}
          width='100%'
        />
        <SrBox marginTop='4px'>
          <SrText opacity='0.6'>
            We use Kwikset smart-electronic locks. The wifi-enabled device placed in the home enables
            the locks to connect directly to the Sunroom server and database.
          </SrText>
        </SrBox>
        <SrBox marginTop='32px'>
          <img
            width={isSmallScreen ? '100%' : '450px'}
            height='auto'
            alt='sunlock'
            src='/images/sunlock-unlock.gif'
            style={{ borderRadius: '14px' }}
          />
        </SrBox>
      </SrBox>
    </SrModalV2>
  );
};

SrSunlocksSelfTouringModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

SrSunlocksSelfTouringModal.displayName = 'SrSunlocksSelfTouringModal';

export default SrSunlocksSelfTouringModal;
